<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { Plus } from '@element-plus/icons-vue'
import type { FormInstance, FormItemInstance, UploadFile } from 'element-plus'
import { ref, reactive, onBeforeUnmount, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { useClaims } from '@/composables/asicw/claims'
import type { IPolicy } from '@/types/asicw/policies'
import {
  ClaimFailurePart,
  type IClaimSubmission,
  type IFailedPart,
  type IFailedParts,
  type IClaimContainer,
} from '@/types/asicw/claims'
import AsicwPolicySelect from '@/components/asicw/common/AsicwPolicySelect.vue'
import { getFailedPartIcon } from '@/components/asicw/common/utils'
import { ContainerType, ContainerTypeSelectOptions } from '@/types/rc/containers'
import { vMaska } from 'maska/vue'
import { NotifyFail } from '@/helpers/core/notifications'

declare const URL: typeof window.URL

const emit = defineEmits(['update:modelValue'])

const props = defineProps<{
  modelValue: boolean
  preSelectedPolicy?: IPolicy
}>()

const { createClaimMutation } = useClaims()

const formRef = ref<FormInstance>()
const policyRef = ref<FormItemInstance>()
const imageList = ref<UploadFile[]>([])
const logFileList = ref<UploadFile[]>([])
const isSubmitting = ref(false)
const claimsList = ref<IClaimSubmission[]>([])
const defaultContainerDimensions = {
  type: ContainerType.Box,
  length: 0.0,
  width: 0.0,
  height: 0.0,
  weight: 0.0,
}

// Generic failure reasons - to be moved to config/endpoint later
const FAILURE_REASONS = [
  'No Power',
  'Overheating',
  'Physical Damage',
  'Not Detected',
  'Malfunction',
  'Short Circuit',
  'Broken Connection',
  'Performance Issues',
  'Unusual Noise',
  'Other',
]

const failedPartsInitialState = () =>
  Object.values(ClaimFailurePart).reduce((acc, part) => {
    acc[part] = { enabled: false, reason: '', part_sn: undefined, part_model: undefined, part_number: undefined }
    return acc
  }, {} as IFailedParts)

// Initialize form with preSelectedPolicy if provided
const form = reactive({
  policy: props.preSelectedPolicy,
  date_of_loss: '',
  failed_parts: failedPartsInitialState(),
  note: '',
  images: [] as File[],
  log_files: [] as File[],
  container: undefined as IClaimContainer | undefined,
})

// Watch for changes in preSelectedPolicy
watch(
  () => props.preSelectedPolicy,
  (newPolicy) => {
    if (newPolicy) {
      form.policy = newPolicy
    }
  }
)

// Check if Hashboard is enabled as a failed part
const hasHashboardFailure = computed(() => {
  return form.failed_parts[ClaimFailurePart.Hashboard]?.enabled === true
})

// Handle failed part switch change
const handleFailedPartChange = (partKey: string, isEnabled: boolean) => {
  formRef.value?.validateField(['log_files', 'failed_parts'])

  if (partKey === ClaimFailurePart.Hashboard) {
    if (isEnabled && !form.container) {
      // If Hashboard part is enabled, create default container
      form.container = structuredClone(defaultContainerDimensions)
    } else if (!isEnabled) {
      // If Hashboard part is disabled, remove container
      form.container = undefined
    }
  }

  // Run the part validation when the failed part is disabled
  if (!isEnabled) {
    formRef.value?.validateField(`failed_parts.${partKey}`)
  }
}

const failedPartRule = {
  required: true,
  validator: (
    rule: any,
    value: { enabled: boolean; reason: string; part_sn?: string; part_model?: string; part_number?: number },
    callback: (message?: string) => void
  ) => {
    if (!value.enabled) {
      callback()
      return
    }

    if (!value.reason) {
      callback('Please select a reason')
    }

    // Get the part type from the rule path (e.g., "failed_parts.Hashboard" -> "Hashboard")
    const partType = rule?.field?.split('.')[1] as ClaimFailurePart

    // Power Supply and Control Board: part_sn and part_model are mandatory
    if (partType === ClaimFailurePart.PowerSupply || partType === ClaimFailurePart.ControlBoard) {
      if (!value.part_sn?.trim()) {
        callback('Serial Number is required for this part type')
        return
      }
      if (!value.part_model?.trim()) {
        callback('Model is required for this part type')
        return
      }
    }

    // Fan: part_model is mandatory, and either part_sn or part_number is required
    if (partType === ClaimFailurePart.Fan) {
      if (!value.part_sn?.trim() && !value.part_number) {
        callback('Either Serial Number or Number is required for Fan parts')
        return
      }
      if (!value.part_model?.trim()) {
        callback('Model is required for Fan parts')
        return
      }
    }

    callback()
  },
  trigger: ['blur'],
}

const dimensionRules = {
  validator: (rule: unknown, value: any, callback: (message?: string) => void) => {
    if (value.length <= 0 || value.width <= 0 || value.height <= 0 || value.weight <= 0) {
      callback('Some of the dimensions are invalid')
      return
    }
    callback()
  },
  trigger: ['blur', 'change', 'submit'],
}

const formRules = {
  policy: [{ required: true, message: 'Please select a policy', trigger: 'change' }],
  date_of_loss: [{ required: true, message: 'Please select a date of loss', trigger: 'change' }],
  failed_parts: [
    {
      validator: (rule: unknown, value: IFailedParts, callback: (message?: string) => void) => {
        const enabledParts = Object.values(value).filter((part) => part.enabled)
        if (enabledParts.length === 0) {
          callback('Please select at least one failed part')
          return
        }

        callback()
      },
      trigger: ['change', 'blur'],
    },
  ],
  log_files: [
    {
      validator: (rule: unknown, value: File[], callback: (message?: string) => void) => {
        // Check if there are any enabled failed parts
        const enabledParts = Object.values(form.failed_parts).filter((part) => part.enabled)
        if (enabledParts.length === 0) {
          callback()
          return
        }

        // Check if any enabled part is Hashboard
        const hasHashboard = Object.entries(form.failed_parts).some(
          ([part, details]) => details.enabled && part === ClaimFailurePart.Hashboard
        )

        // If Hashboard is enabled, require at least one log file
        if (hasHashboard && value.length === 0) {
          callback('Log files are required for Hashboard failures')
          return
        }

        callback()
      },
      trigger: ['change', 'blur', 'submit'],
    },
  ],
}

const imagePreviewUrl = ref('')
const imagePreviewVisible = ref(false)

const imageUrlCache = new Map<string, string>()

const getImageUrl = (image: File) => {
  if (!image) return ''

  if (!imageUrlCache.has(image.name)) {
    const url = URL.createObjectURL(image)
    imageUrlCache.set(image.name, url)
  }
  return imageUrlCache.get(image.name) || ''
}

// Cleanup object URLs when component is destroyed
onBeforeUnmount(() => {
  imageUrlCache.forEach((url) => {
    URL.revokeObjectURL(url)
  })
  imageUrlCache.clear()
})

const removeClaim = (index: number) => {
  const claim = claimsList.value[index]
  // Cleanup image URLs for the removed claim
  if (claim.pictures) {
    claim.pictures.forEach((image: File) => {
      const url = imageUrlCache.get(image.name)
      if (url) {
        URL.revokeObjectURL(url)
        imageUrlCache.delete(image.name)
      }
    })
  }
  claimsList.value.splice(index, 1)
}

const validateImage = (file: File) => {
  const isImage = file.type === 'image/jpeg' || file.type === 'image/png'
  const fileInMB = file.size / (1024 * 1024)
  const isLt10M = fileInMB < 10

  if (!isImage) {
    ElMessage.error('Upload file must be a JPG or PNG image')
    return false
  }
  if (!isLt10M) {
    ElMessage.error('Image size can not exceed 10MB')
    return false
  }
  return true
}

const validateLogFile = (file: File) => {
  const allowedTypes = [
    'text/plain',
    'application/zip',
    'application/x-zip-compressed',
    'application/vnd.rar',
    'application/x-rar-compressed',
  ]
  const isLogFile = allowedTypes.includes(file.type)
  const fileInMB = file.size / (1024 * 1024)
  const isLt10M = fileInMB < 10

  if (!isLogFile) {
    ElMessage.error('Upload file must be a TXT, ZIP or RAR file')
    return false
  }
  if (!isLt10M) {
    ElMessage.error('Log file size can not exceed 10MB')
    return false
  }
  return true
}

const handleImageChange = (file: UploadFile, fileList: UploadFile[]) => {
  if (file.raw && validateImage(file.raw)) {
    form.images.push(file.raw)
  } else {
    // Remove the invalid file from fileList
    const index = fileList.findIndex((f) => f.uid === file.uid)
    if (index !== -1) {
      fileList.splice(index, 1)
    }
  }
}

const handleImageRemove = (file: UploadFile) => {
  const index = form.images.findIndex((f) => f.name === file.name)
  if (index !== -1) {
    form.images.splice(index, 1)
  }
}

const handleLogFileChange = (file: UploadFile, fileList: UploadFile[]) => {
  if (file.raw && validateLogFile(file.raw)) {
    form.log_files.push(file.raw)
  } else {
    // Remove the invalid file from fileList
    const index = fileList.findIndex((f) => f.uid === file.uid)
    if (index !== -1) {
      fileList.splice(index, 1)
    }
  }

  formRef.value?.validateField('log_files')
}

const handleLogFileRemove = (file: UploadFile) => {
  const index = form.log_files.findIndex((f) => f.name === file.name)
  if (index !== -1) {
    form.log_files.splice(index, 1)
  }

  formRef.value?.validateField('log_files')
}

const handleLogFileExceed = () => {
  ElMessage.warning('Maximum 5 files allowed')
}

// Update addClaimToList to use the selected policy directly
const addClaimToList = async () => {
  if (!formRef.value) return

  if (claimsList.value.some((claim) => claim.policy_id == form.policy?.id)) {
    NotifyFail('You have already added a claim for this policy. Please review the table.')
    return
  }

  await formRef.value.validate(async (valid, fields?: Record<string, unknown>) => {
    if (valid) {
      if (!form.policy) return

      claimsList.value.push({
        customer: form.policy.customer,
        model_family: form.policy.insured_miner.model_family,
        serial_number: form.policy.insured_miner.sn,
        policy_id: form.policy.id,
        date_of_loss: form.date_of_loss,
        failed_parts: { ...form.failed_parts },
        note: form.note,
        pictures: [...form.images],
        log_files: [...form.log_files],
        container: form.container,
      })

      // Reset form
      form.policy = undefined
      form.date_of_loss = ''
      Object.values(ClaimFailurePart).forEach((part) => {
        form.failed_parts[part] = { enabled: false, reason: '' }
      })
      form.note = ''
      form.images = []
      form.log_files = []
      form.container = undefined
      imageList.value = []
      logFileList.value = []
      policyRef.value?.resetField()
    } else if (fields) {
      // Get the first invalid field
      const invalidEl = document.querySelector('.el-form-item.is-error')
      if (invalidEl) {
        invalidEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  })
}

const handleSubmit = async () => {
  if (claimsList.value.length === 0) {
    ElMessage.warning('Please add at least one claim to the list')
    return
  }

  try {
    isSubmitting.value = true
    const claims = claimsList.value.map((claim) => ({
      policy_id: claim.policy_id,
      date_of_loss: claim.date_of_loss,
      failures: Object.entries(claim.failed_parts)
        .filter(([, details]) => details.enabled)
        .map(([part, details]) => ({
          part,
          part_sn: details.part_sn,
          part_model: details.part_model,
          part_number: details.part_number,
          reason: details.reason,
        })),
      notes: claim.note || undefined,
      pictures: claim.pictures || undefined,
      log_files: claim.log_files || undefined,
      container: claim.container,
    }))

    await createClaimMutation.mutateAsync(claims)
    emit('update:modelValue', false)
  } catch {
    // Error is handled by mutation error handler
  } finally {
    isSubmitting.value = false
  }
}

const handlePictureCardPreview = (uploadFile: UploadFile) => {
  imagePreviewUrl.value = URL.createObjectURL(uploadFile.raw!)
  imagePreviewVisible.value = true
}

const handleDialogClosed = () => {
  policyRef.value?.resetField()
  formRef.value?.resetFields()
  claimsList.value = []
  form.container = undefined
  form.note = ''
  form.date_of_loss = ''
  form.images = []
  form.log_files = []
  form.failed_parts = failedPartsInitialState()
  imageList.value = []
  logFileList.value = []
  imagePreviewUrl.value = ''
  imagePreviewVisible.value = false
}
</script>

<template>
  <el-dialog
    :model-value="modelValue"
    title="Submit claims"
    width="95%"
    class="claims-dialog"
    align-center
    :show-close="false"
    @update:model-value="$emit('update:modelValue', $event)"
    @closed="handleDialogClosed"
  >
    <template #header>
      <div class="claims-dialog__header">
        <h3 class="claims-dialog__header-title">Submit claims</h3>
        <div class="claims-dialog__header-buttons">
          <el-button link @click="$emit('update:modelValue', false)">
            <Icon icon="material-symbols:close" :height="20" />
          </el-button>
        </div>
      </div>
    </template>

    <div class="claims-dialog__content">
      <!-- Left Column - Form -->
      <div class="claims-dialog__form">
        <el-scrollbar max-height="75vh" style="padding-right: var(--spacing-spacing-sm)">
          <el-form
            ref="formRef"
            :model="form"
            :rules="formRules"
            label-position="top"
            label-width="auto"
            class="claims-dialog__form-content"
          >
            <el-form-item ref="policyRef" label="Policy" prop="policy" class="mb-10">
              <AsicwPolicySelect
                v-model="form.policy"
                :excluded-policy-ids="claimsList.map((claim) => claim.policy_id)"
              />
            </el-form-item>

            <el-form-item label="Date of Loss" prop="date_of_loss" class="mb-10">
              <el-date-picker
                v-model="form.date_of_loss"
                value-format="YYYY-MM-DDTHH:mm:ss.SSSZ"
                type="date"
                placeholder="Select date of loss"
                style="width: 100%"
                :disabled-date="(time: Date) => time.getTime() > Date.now()"
              />
            </el-form-item>

            <el-form-item label="Failed parts" prop="failed_parts" required class="mb-10">
              <el-form-item
                v-for="(part, key) in form.failed_parts"
                :key="key"
                :prop="`failed_parts.${key}`"
                :rules="failedPartRule"
                class="claims-dialog__failed-parts-item"
              >
                <div class="claims-dialog__failed-parts-group">
                  <div class="claims-dialog__failed-parts-header">
                    <Icon :icon="getFailedPartIcon(key)" :height="20" />
                    <span class="claims-dialog__failed-parts-label">{{ key }}</span>
                    <el-switch
                      v-model="part.enabled"
                      class="ml-auto"
                      @change="(value: any) => handleFailedPartChange(key as string, value as boolean)"
                    />
                  </div>
                  <el-collapse-transition>
                    <div v-if="part.enabled" class="claims-dialog__failed-parts-fields">
                      <el-select
                        v-model="part.reason"
                        placeholder="Select reason"
                        class="claims-dialog__failed-parts-select"
                        filterable
                        allow-create
                      >
                        <el-option v-for="reason in FAILURE_REASONS" :key="reason" :label="reason" :value="reason" />
                      </el-select>

                      <el-input
                        v-model="part.part_sn"
                        placeholder="Serial Number"
                        clearable
                        class="claims-dialog__failed-parts-input"
                      />

                      <el-input
                        v-model="part.part_model"
                        placeholder="Model"
                        clearable
                        class="claims-dialog__failed-parts-input"
                      />

                      <el-input-number
                        v-if="key === ClaimFailurePart.Fan"
                        v-model="part.part_number"
                        clearable
                        placeholder="Number"
                        class="claims-dialog__failed-parts-input"
                        :min="1"
                        :max="4"
                        :precision="0"
                        controls-position="right"
                      />
                    </div>
                  </el-collapse-transition>
                </div>
              </el-form-item>
            </el-form-item>

            <el-form-item label="Images (optional)" prop="images" :required="false" class="mb-10">
              <el-upload
                v-model:file-list="imageList"
                list-type="picture-card"
                :auto-upload="false"
                :on-change="handleImageChange"
                :on-remove="handleImageRemove"
                :on-preview="handlePictureCardPreview"
                multiple
                accept="image/jpeg,image/png"
              >
                <Icon icon="ep:plus" :height="20" />
                <template #tip>
                  <div class="el-upload__tip">JPG, JPEG or PNG files with a size less than 10MB</div>
                </template>
              </el-upload>
              <el-image-viewer
                v-if="imagePreviewVisible"
                :url-list="[imagePreviewUrl]"
                @close="imagePreviewVisible = false"
              />
            </el-form-item>

            <el-form-item label="Log Files (optional)" prop="log_files" :required="false" class="mb-10">
              <el-upload
                v-model:file-list="logFileList"
                list-type="text"
                :auto-upload="false"
                :on-change="handleLogFileChange"
                :on-remove="handleLogFileRemove"
                :on-exceed="handleLogFileExceed"
                multiple
                :limit="5"
                accept="text/plain,application/zip,application/x-zip-compressed,application/vnd.rar,application/x-rar-compressed"
              >
                <el-button>Upload</el-button>
                <template #tip>
                  <div class="el-upload__tip">TXT, ZIP or RAR files with a size less than 10MB</div>
                </template>
              </el-upload>
            </el-form-item>

            <el-form-item v-if="hasHashboardFailure" prop="container" :rules="dimensionRules" class="mb-10">
              <template #label>
                <div class="flex flex-row gap-1 items-center">
                  <span>Package Dimensions</span>
                  <el-tooltip content="How are you planning to pack your miner?" placement="right" effect="light">
                    <Icon icon="material-symbols:help-outline" height="16" />
                  </el-tooltip>
                </div>
              </template>
              <div class="flex flex-col gap-3">
                <el-form-item prop="type" label="Type" class="dimensions-label">
                  <el-select v-model="form.container!.type">
                    <el-option
                      v-for="option in ContainerTypeSelectOptions"
                      :key="option.key"
                      :label="option.value"
                      :value="option.value"
                    />
                  </el-select>
                </el-form-item>
                <div class="flex gap-4">
                  <el-form-item label="Length (in)" prop="length" class="dimensions-label">
                    <el-input
                      v-model="form.container!.length"
                      v-maska
                      data-maska="9999.9"
                      data-maska-tokens="9:\d:optional"
                      placeholder="0.0"
                      class="w-20"
                      @input="formRef?.validateField('container')"
                    />
                  </el-form-item>
                  <el-form-item label="Width (in)" prop="width" class="dimensions-label">
                    <el-input
                      v-model="form.container!.width"
                      v-maska
                      data-maska="9999.9"
                      data-maska-tokens="9:\d:optional"
                      placeholder="0.0"
                      class="w-20"
                      @input="formRef?.validateField('container')"
                    />
                  </el-form-item>
                  <el-form-item label="Height (in)" prop="height" class="dimensions-label">
                    <el-input
                      v-model="form.container!.height"
                      v-maska
                      data-maska="9999.9"
                      data-maska-tokens="9:\d:optional"
                      placeholder="0.0"
                      class="w-20"
                      @input="formRef?.validateField('container')"
                    />
                  </el-form-item>
                  <el-form-item label="Weight (lb)" prop="weight" class="dimensions-label">
                    <el-input
                      v-model="form.container!.weight"
                      v-maska
                      data-maska="9999.9"
                      data-maska-tokens="9:\d:optional"
                      placeholder="0.0"
                      class="w-20"
                      @input="formRef?.validateField('container')"
                    />
                  </el-form-item>
                </div>
              </div>
            </el-form-item>

            <el-form-item label="Note" prop="note">
              <el-input v-model="form.note" type="textarea" :rows="3" placeholder="Enter note (optional)" />
            </el-form-item>
          </el-form>
        </el-scrollbar>
        <el-button :icon="Plus" class="claims-dialog__form-add-button" @click="addClaimToList"
          >Add claim to list</el-button
        >
      </div>

      <!-- Right Column - Table -->
      <div class="claims-dialog__table">
        <el-table :data="claimsList" style="width: 100%" height="100%">
          <template #empty>
            <el-empty />
          </template>
          <el-table-column label="Customer" min-width="150">
            <template #default="scope">
              <span>{{ scope.row.customer?.name || 'N/A' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="model_family" label="Model Family" />
          <el-table-column prop="serial_number" label="SN" />
          <el-table-column prop="failed_parts" label="Failed part(s)" min-width="150">
            <template #default="scope">
              <el-tooltip placement="right" :show-after="200" :hide-after="0" effect="light">
                <template #content>
                  <div class="claims-dialog__failure-details">
                    <div v-for="[part, details] in Object.entries<IFailedPart>(scope.row.failed_parts)" :key="part">
                      <template v-if="details.enabled">
                        <strong>{{ part.toUpperCase() }}:</strong> {{ details.reason }}
                      </template>
                    </div>
                  </div>
                </template>
                <div class="claims-dialog__text-truncate">
                  <div
                    v-for="part in Object.entries(scope.row.failed_parts)
                      .filter(([, details]: [string, any]) => details.enabled)
                      .map(([key]) => key.toUpperCase())"
                    :key="part"
                  >
                    {{ part }}
                  </div>
                </div>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="Package Dimensions" min-width="150">
            <template #default="scope">
              <span v-if="scope.row.container"
                >{{ scope.row.container.length }}x{{ scope.row.container.width }}x{{ scope.row.container.height }} ({{
                  scope.row.container.weight
                }}
                lb)</span
              >
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="note" label="Notes" min-width="150">
            <template #default="scope">
              <el-tooltip
                v-if="scope.row.note"
                :content="scope.row.note"
                placement="top"
                :show-after="200"
                :hide-after="0"
                effect="light"
              >
                <div class="claims-dialog__text-truncate">{{ scope.row.note }}</div>
              </el-tooltip>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="pictures" label="Images" width="100">
            <template #default="scope">
              <el-tooltip
                v-if="scope.row.pictures?.length"
                placement="bottom"
                :show-after="200"
                :hide-after="250"
                effect="light"
              >
                <template #content>
                  <div class="claims-dialog__image-preview">
                    <img
                      v-for="(image, index) in scope.row.pictures"
                      :key="index"
                      :src="getImageUrl(image)"
                      class="claims-dialog__image-preview-thumbnail"
                      alt="Preview"
                      loading="lazy"
                    />
                  </div>
                </template>
                <span class="px-2.5 py-0.5 rounded-full bg-gray-100/20 border-1 border-white">{{
                  scope.row.pictures?.length || 0
                }}</span>
              </el-tooltip>
              <span v-else>0</span>
            </template>
          </el-table-column>
          <el-table-column prop="log_files" label="Log Files" width="150">
            <template #default="scope">
              <div v-if="scope.row.log_files?.length" class="claims-dialog__log-files-list">
                <div v-for="(file, index) in scope.row.log_files" :key="index" class="claims-dialog__log-file-item">
                  • {{ file.name }}
                </div>
              </div>
              <span v-else>0</span>
            </template>
          </el-table-column>
          <el-table-column width="60" fixed="right">
            <template #default="scope">
              <el-tooltip content="Remove" placement="top" effect="light">
                <el-button link title="Delete" @click="removeClaim(scope.$index)">
                  <Icon icon="material-symbols:delete-outline" :height="16" />
                </el-button>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">Cancel</el-button>
      <el-button type="primary" :loading="isSubmitting" :disabled="claimsList.length == 0" @click="handleSubmit"
        >Submit</el-button
      >
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.claims-dialog {
  max-height: 95vh;
  display: flex;
  flex-direction: column;

  .el-dialog__body {
    height: calc(95vh - 40px);
    padding: 0;
    flex: 1;
    overflow: hidden;
  }

  .el-dialog__header {
    margin: 0;
    padding: 0;
    flex-shrink: 0;
  }

  .el-dialog__footer {
    display: none;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
    flex-shrink: 0;

    &-title {
      margin: 0;
    }

    &-buttons {
      display: flex;
      gap: var(--spacing-spacing-sm);
    }
  }

  &__content {
    display: grid;
    grid-template-columns: 1fr 3fr;
    gap: var(--spacing-spacing-xl);
    padding: 20px;
    height: 100%;
  }

  &__form {
    border-right: 1px solid var(--border-color);
    padding-right: var(--spacing-spacing-xl);
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-spacing-sm);

    &-content {
      flex: 1;
      overflow-y: auto;
      padding-right: var(--spacing-spacing-sm);
    }

    &-button {
      margin-top: auto;
    }

    &-add-button {
      width: 100%;
      background-color: var(--el-fill-color-light);
    }
  }

  &__failed-parts {
    &-group {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-spacing-md);
      width: 100%;
    }

    &-item {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-spacing-sm);
      margin-bottom: 1rem !important;
    }

    &-header {
      display: flex;
      align-items: center;
      width: 100%;
      gap: var(--spacing-spacing-sm);
    }

    &-label {
      font-size: var(--font-size-body);
      font-weight: 500;
    }

    &-select {
      width: 100%;
    }

    &-fields {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-spacing-sm);
    }

    &-input {
      width: 100%;
    }
  }

  &__failure-details {
    display: flex;
    flex-direction: column;
    gap: 4px;

    strong {
      margin-right: 4px;
    }
  }

  &__table {
    overflow-y: auto;
    height: 100%;
  }

  &__text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }

  &__image-preview {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 8px;
    padding: 8px;
    max-width: 320px;

    &-thumbnail {
      width: 100px;
      height: 100px;
      object-fit: cover;
      border-radius: 4px;
    }
  }

  &__log-files-list {
    display: flex;
    flex-direction: column;
    gap: 2px;

    &-item {
      font-size: var(--font-size-sm);
      color: var(--el-text-color-primary);
      overflow-wrap: break-word;
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.dimensions-label :deep(.el-form-item__label) {
  font-size: var(--font-size-sm);
}

:deep(.el-form-item__label) {
  font-weight: bold;
}
</style>
